import * as z from 'zod';

// Strapi
import { contact, blocks, seo } from '@/services/strapiPopulate';
import { fetchStrapiContentWithBlocks } from '@/services/blocks/fetchStrapiContentWithBlocks';

// ZOD
import { ContactZod, ContactType } from '@/views/Contact/ContactType';

// Client
import client from '../client';

const ContactDataZod = z.object({
  firstname: z.string(),
  lastname: z.string(),
  email: z.string(),
  job: z.string().optional(),
  company: z.string().optional(),
  phone: z.string().optional(),
  expertise: z.string().optional(),
  type: z.string().optional(),
  budget: z.string().optional(),
  comment: z.string().optional(),
  marketingConsent: z.boolean(),
  isUrgent: z.boolean().optional(),
  files: z.array(z.instanceof(File)).optional(),
});

const ContactOriginDataZod = z.object({
  id: z.string(),
  origin: z.string(),
});

export type ContactDataType = z.infer<typeof ContactDataZod>;
export type ContactOriginDataType = z.infer<typeof ContactOriginDataZod>;

export const getContact = async (): Promise<ContactType> =>
  fetchStrapiContentWithBlocks(
    '/api/contact',
    ContactZod,
    {
      ...contact,
      blocks,
      seo,
    },
    'Contact Page',
  );

/**
 * Post contact form data
 */
export const postContact = async (
  data: ContactDataType,
): Promise<ContactDataType & { data: { documentId: string } }> => {
  try {
    await ContactDataZod.parseAsync(data);

    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      const typedKey = key as keyof ContactDataType;
      if (Array.isArray(data[typedKey])) {
        (data[typedKey] as File[]).forEach((file) => formData.append(key, file));
      } else if (data[typedKey] === null) {
        formData.append(key, '');
      } else {
        formData.append(key, String(data[typedKey]));
      }
    });

    const response = await client.post('/api/form-contact/submit', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response?.data;
  } catch (error) {
    console.error('Error posting contact:', error);
    throw error;
  }
};

/**
 * Update contact origin
 */
export const putContactOrigin = async (data: ContactOriginDataType): Promise<ContactOriginDataType> => {
  try {
    await ContactOriginDataZod.parseAsync(data);
    const response = await client.put(`/api/form-contact/${data.id}/origin`, {
      data: { origin: data.origin },
    });
    return response?.data;
  } catch (error) {
    console.error('Error updating contact origin:', error);
    throw error;
  }
};
